// src/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import api from './api';
//import jwtDecode from 'jwt-decode'; // npm install jwt-decode
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  // user = { username, role } or null
  const [user, setUser] = useState(null);
  // loading flag so we don’t redirect to login before rehydration
  const [loading, setLoading] = useState(true);

  // 1) On mount, check localStorage for an existing token
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      try {
        // jwtDecode will extract { userId, role, exp, iat } from the token payload
        const { role, exp } = jwtDecode(token);

        // If token is expired, remove it
        if (Date.now() >= exp * 1000) {
          localStorage.removeItem('jwtToken');
          setUser(null);
        } else {
          // We don’t store username in the token payload, so you can either:
          // a) Decode another field if you added it, e.g. { username, role }
          // b) Make a quick /api/auth/me call to get current user’s data
          // For simplicity, let’s assume you included "username" in your JWT payload:
          const { username } = jwtDecode(token);
          setUser({ username, role });
          // Also set the default Authorization header if you like:
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      } catch (err) {
        console.warn('Invalid token in storage:', err);
        localStorage.removeItem('jwtToken');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  // 2) Wrap login() to save token + set user
  const login = async (username, password) => {
    const resp = await api.post('/auth/login', { username, password });
    const { token, role } = resp.data;
    localStorage.setItem('jwtToken', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser({ username, role });
  };

  const logout = () => {
    localStorage.removeItem('jwtToken');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // 3) While we’re rehydrating from localStorage, don’t render children
  if (loading) {
    return <div>Loading…</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
